import { useState } from "react";
import {
  Title,
  Text,
  Stack,
  Group,
  Paper,
  Button,
  Modal,
  TextInput,
  MultiSelect,
  Badge,
  Table,
  ActionIcon,
  Tooltip,
  Alert,
  Code,
  Loader,
  Center,
  CopyButton,
  ScrollArea,
} from "@mantine/core";
import {
  IconKey,
  IconPlus,
  IconTrash,
  IconBan,
  IconAlertTriangle,
  IconCopy,
  IconCheck,
  IconShieldLock,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { apiKeysApi } from "../../api/apiKeys.api";
import { modulesApi } from "../../api/modules.api";
import { apiError } from "../../api/client";

export default function ApiKeysPage() {
  const qc = useQueryClient();

  const keysQ = useQuery({
    queryKey: ["api-keys"],
    queryFn: apiKeysApi.list,
  });
  const modulesQ = useQuery({
    queryKey: ["modules", "all"],
    queryFn: modulesApi.listAll,
  });

  const [createOpen, setCreateOpen] = useState(false);

  const revokeMut = useMutation({
    mutationFn: (id) => apiKeysApi.revoke(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "API key revoked" });
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (e) => notifications.show({ color: "red", message: apiError(e) }),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => apiKeysApi.delete(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "API key deleted" });
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (e) => notifications.show({ color: "red", message: apiError(e) }),
  });

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>API Keys</Title>
          <Text c="dimmed">
            Issue tokens for external systems (e.g. Duvo) to call inbound module
            webhooks. Plaintext is shown exactly once at creation.
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setCreateOpen(true)}
        >
          New API Key
        </Button>
      </Group>

      {keysQ.error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {apiError(keysQ.error)}
        </Alert>
      )}

      <Paper p={0}>
        <ScrollArea>
          <Table withTableBorder verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Prefix</Table.Th>
                <Table.Th>Modules</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last used</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {keysQ.isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Center py="lg">
                      <Loader />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (keysQ.data || []).length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Center py="lg">
                      <Text size="sm" c="dimmed">
                        No API keys yet. Click "New API Key" to issue one.
                      </Text>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (
                keysQ.data.map((k) => (
                  <Table.Tr key={k.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <IconKey size={14} />
                        <Text fw={500}>{k.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Code>dmw_{k.prefix}_…</Code>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {(k.modules || []).map((m) => (
                          <Badge key={m} variant="light" color="indigo" size="sm">
                            {m}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {k.isActive ? (
                        <Badge color="green" variant="light">
                          active
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light">
                          revoked
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {k.lastUsedAt ? (
                        <Text size="xs">
                          {new Date(k.lastUsedAt).toLocaleString()}
                          {k.lastUsedIp && (
                            <Text size="xs" c="dimmed">
                              {k.lastUsedIp}
                            </Text>
                          )}
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          never
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {new Date(k.createdAt).toLocaleDateString()}
                      </Text>
                      {k.createdBy && (
                        <Text size="xs" c="dimmed">
                          {k.createdBy.fullName}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end">
                        {k.isActive && (
                          <Tooltip label="Revoke (disables the key)">
                            <ActionIcon
                              variant="subtle"
                              color="orange"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Revoke API key "${k.name}"? External callers will start receiving 401s.`
                                  )
                                ) {
                                  revokeMut.mutate(k.id);
                                }
                              }}
                            >
                              <IconBan size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label="Delete permanently">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Delete API key "${k.name}" permanently? This cannot be undone.`
                                )
                              ) {
                                deleteMut.mutate(k.id);
                              }
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      <CreateKeyModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        modules={modulesQ.data || []}
      />
    </Stack>
  );
}

function CreateKeyModal({ opened, onClose, modules }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [moduleKeys, setModuleKeys] = useState([]);
  const [issued, setIssued] = useState(null);

  const createMut = useMutation({
    mutationFn: () =>
      apiKeysApi.create({ name: name.trim(), modules: moduleKeys }),
    onSuccess: (data) => {
      setIssued(data);
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  const handleClose = () => {
    setName("");
    setModuleKeys([]);
    setIssued(null);
    onClose();
  };

  const moduleOptions = (modules || []).map((m) => ({
    value: m.key,
    label: m.name,
  }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        issued ? (
          <Group gap="xs">
            <IconShieldLock size={18} />
            <Text fw={600}>Save this key now</Text>
          </Group>
        ) : (
          "New API Key"
        )
      }
      size="lg"
      closeOnClickOutside={!issued}
      withCloseButton={!!issued || !createMut.isPending}
    >
      {issued ? (
        <Stack gap="md">
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            This is the only time the full key will be shown. Copy it into the
            caller's configuration now — we only store its hash.
          </Alert>
          <Paper p="sm" withBorder>
            <Stack gap={6}>
              <Text size="xs" c="dimmed">
                Full API key
              </Text>
              <Group gap="xs" wrap="nowrap">
                <Code
                  style={{
                    flex: 1,
                    fontSize: 13,
                    wordBreak: "break-all",
                    padding: "8px 12px",
                  }}
                >
                  {issued.plaintext}
                </Code>
                <CopyButton value={issued.plaintext}>
                  {({ copied, copy }) => (
                    <Button
                      size="sm"
                      variant={copied ? "filled" : "light"}
                      color={copied ? "teal" : "indigo"}
                      leftSection={
                        copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )
                      }
                      onClick={copy}
                    >
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </Stack>
          </Paper>
          <Paper p="sm" withBorder>
            <Stack gap={6}>
              <Text size="xs" c="dimmed">
                Usage (HTTP header)
              </Text>
              <Code>X-API-Key: {issued.plaintext}</Code>
            </Stack>
          </Paper>
          <Group justify="flex-end">
            <Button onClick={handleClose}>I have saved it</Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g. Duvo production webhook"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            maxLength={120}
          />
          <MultiSelect
            label="Allowed modules"
            description="The key can call inbound webhooks for these modules only."
            data={moduleOptions}
            value={moduleKeys}
            onChange={setModuleKeys}
            searchable
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              loading={createMut.isPending}
              disabled={!name.trim() || moduleKeys.length === 0}
              onClick={() => createMut.mutate()}
            >
              Generate key
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
